/**
 * AssessmentConsentStep — tela de consentimento LGPD compartilhada
 *
 * Exibida ANTES do início de qualquer assessment público (IGA, BS4IT, etc.).
 * Coleta 3 consentimentos (1 obrigatório, 2 opcionais) com base legal LGPD art. 7º I e IX.
 *
 * Estilo idêntico aos forms existentes do BS4IT (slate-950 + cyan-400).
 */
import { useState } from 'react'
import { Mail, MessageCircle, BookOpen, ArrowRight } from 'lucide-react'

export type ConsentValues = {
  relatorio: boolean
  contato: boolean
  newsletter: boolean
}

type Props = {
  /** Parceiro/marca que aparece no label do consentimento 2. */
  partner: 'BS4IT' | 'NAI'
  /** Callback chamado quando o usuário aceita e clica em "Aceitar e iniciar diagnóstico". */
  onAccept: (consents: ConsentValues) => void
  /** Valores iniciais (opcional — útil para hidratar de localStorage). */
  initial?: Partial<ConsentValues>
}

export default function AssessmentConsentStep({ partner, onAccept, initial }: Props) {
  const [relatorio, setRelatorio] = useState<boolean>(initial?.relatorio ?? false)
  const [contato, setContato] = useState<boolean>(initial?.contato ?? false)
  const [newsletter, setNewsletter] = useState<boolean>(initial?.newsletter ?? false)

  const partnerLabel = partner === 'BS4IT' ? 'BS4IT e a Nai S/A' : 'Nai S/A'
  const canSubmit = relatorio === true

  return (
    <section className="animate-fadeIn">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/40 text-cyan-300 text-[11px] font-bold uppercase tracking-widest mb-3">
            LGPD · Consentimento informado
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white mb-3">
            Antes de começar, precisamos do seu consentimento
          </h1>
          <p className="text-sm text-slate-300 leading-relaxed">
            Este diagnóstico é gratuito e dura cerca de 15 — 20 minutos. Para gerar e
            enviar seu relatório, precisamos tratar alguns dados pessoais (nome,
            e-mail corporativo, telefone e respostas do questionário).
          </p>
          <p className="text-xs text-slate-400 mt-2">
            Base legal: <span className="text-slate-200 font-semibold">consentimento (LGPD art. 7º, I)</span>{' '}
            e <span className="text-slate-200 font-semibold">legítimo interesse (art. 7º, IX)</span>.
          </p>
        </div>

        <div className="space-y-3 bg-slate-900/40 border border-slate-800 rounded-2xl p-5 sm:p-6">
          <ConsentRow
            icon={<Mail className="h-4 w-4 text-cyan-300" />}
            checked={relatorio}
            onChange={setRelatorio}
            required
            label="Aceito receber por e-mail o relatório de diagnóstico desta avaliação."
            hint="Sem este consentimento não conseguimos lhe entregar o resultado."
          />
          <ConsentRow
            icon={<MessageCircle className="h-4 w-4 text-cyan-300" />}
            checked={contato}
            onChange={setContato}
            label={`Autorizo a ${partnerLabel} a entrar em contato comigo para apresentar soluções relacionadas a este diagnóstico.`}
          />
          <ConsentRow
            icon={<BookOpen className="h-4 w-4 text-cyan-300" />}
            checked={newsletter}
            onChange={setNewsletter}
            label="Autorizo o envio de conteúdo educativo (newsletter, white papers, convites para eventos)."
          />
        </div>

        <div className="mt-5 text-[11px] text-slate-400 leading-relaxed bg-slate-900/30 border border-slate-800 rounded-xl p-4">
          <p>
            <span className="font-semibold text-slate-200">Retenção:</span> seus dados são
            mantidos por <span className="text-slate-200">até 24 meses</span> após o último
            contato. <span className="font-semibold text-slate-200">Direitos do titular</span>{' '}
            (acesso, correção, eliminação, portabilidade, revogação) podem ser exercidos
            a qualquer momento pelo canal{' '}
            <a href="mailto:dpo@nai-it.com" className="text-cyan-300 hover:text-cyan-200 underline">
              dpo@nai-it.com
            </a>
            .
          </p>
        </div>

        <div className="mt-6 flex flex-col sm:flex-row items-center justify-end gap-3">
          <button
            type="button"
            disabled={!canSubmit}
            onClick={() => onAccept({ relatorio, contato, newsletter })}
            className="w-full sm:w-auto px-6 py-3 rounded-full bg-gradient-to-r from-cyan-600 to-emerald-600 text-white font-bold text-sm shadow-lg hover:shadow-xl hover:scale-[1.02] disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100 transition inline-flex items-center justify-center gap-2"
          >
            Aceitar e iniciar diagnóstico
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>
        {!canSubmit && (
          <p className="mt-3 text-[11px] text-slate-500 text-right">
            Marque a 1ª opção (obrigatória) para continuar.
          </p>
        )}
      </div>
    </section>
  )
}

function ConsentRow({
  icon, checked, onChange, label, required, hint,
}: {
  icon: React.ReactNode
  checked: boolean
  onChange: (v: boolean) => void
  label: string
  required?: boolean
  hint?: string
}) {
  return (
    <label className="flex items-start gap-3 cursor-pointer group rounded-xl p-3 border border-slate-800 bg-slate-900/40 hover:border-slate-700 transition">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-1 h-4 w-4 rounded border-slate-600 bg-slate-800 text-cyan-500 focus:ring-cyan-500 focus:ring-offset-slate-950"
      />
      <div className="flex-1">
        <div className="flex items-start gap-2">
          <div className="mt-0.5">{icon}</div>
          <span className="text-sm leading-relaxed text-slate-200 group-hover:text-white">
            {label}
            {required && <span className="text-red-400 ml-1">*</span>}
          </span>
        </div>
        {hint && (
          <p className="ml-6 mt-1 text-[11px] text-slate-500">{hint}</p>
        )}
      </div>
    </label>
  )
}
